import React, { useState, useMemo } from 'react'
import {
  Bell, X, Check, AlertCircle, Info, CheckCircle,
  Clock, Calendar, Trophy, ShoppingCart, School,
  Users, Archive, Trash2
} from 'lucide-react'
import { parseISO, formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
  Notification,
} from '@/hooks/useNotifications'

interface NotificationCenterProps {
  embedded?: boolean
  maxHeight?: string
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  embedded = false,
  maxHeight = '400px'
}) => {
  const [filter, setFilter] = useState<'all' | 'unread'>('unread')

  // React Query hooks
  const { data, isLoading: loading } = useNotifications()
  const markReadMutation = useMarkNotificationRead()
  const markAllReadMutation = useMarkAllNotificationsRead()
  const deleteMutation = useDeleteNotification()

  const allNotifications = data?.notifications || []
  const usingMockData = data?.isMock || false

  // Filter notifications based on filter state
  const notifications = useMemo(() => {
    if (filter === 'unread') {
      return allNotifications.filter(n => !n.read)
    }
    return allNotifications
  }, [allNotifications, filter])

  const markAsRead = async (notificationId: string) => {
    try {
      await markReadMutation.mutateAsync(notificationId)
      toast.success('Marked as read')
    } catch (error) {
      toast.error('Failed to mark as read')
    }
  }

  const markAllAsRead = async () => {
    try {
      await markAllReadMutation.mutateAsync()
      toast.success('All notifications marked as read')
    } catch (error) {
      toast.error('Failed to mark all as read')
    }
  }

  const deleteNotification = async (notificationId: string) => {
    try {
      await deleteMutation.mutateAsync(notificationId)
      toast.success('Notification deleted')
    } catch (error) {
      toast.error('Failed to delete notification')
    }
  }

  const getIcon = (type: string) => {
    switch (type) {
      case 'chore':
        return <CheckCircle className="w-5 h-5" />
      case 'reward':
        return <Trophy className="w-5 h-5" />
      case 'calendar':
        return <Calendar className="w-5 h-5" />
      case 'meal':
        return <ShoppingCart className="w-5 h-5" />
      case 'school':
        return <School className="w-5 h-5" />
      default:
        return <Info className="w-5 h-5" />
    }
  }

  const getIconColor = (type: string, priority: string) => {
    if (priority === 'high') return 'text-red-500'
    if (priority === 'medium') return 'text-yellow-500'
    
    switch (type) {
      case 'chore': return 'text-blue-500'
      case 'reward': return 'text-purple-500'
      case 'calendar': return 'text-green-500'
      case 'meal': return 'text-orange-500'
      case 'school': return 'text-indigo-500'
      default: return 'text-gray-500'
    }
  }

  const unreadCount = notifications.filter(n => !n.read).length

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className={`${!embedded ? 'bg-white rounded-lg shadow-sm' : ''}`}>
      {/* Mock Data Indicator */}
      {usingMockData && (
        <div className={`bg-yellow-50 ${!embedded ? 'border-b border-yellow-200' : 'rounded-lg border border-yellow-200 mb-3'} p-3`}>
          <div className="flex items-center text-sm">
            <AlertCircle className="w-4 h-4 text-yellow-600 mr-2" />
            <span className="text-yellow-800">Using sample notification data</span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className={`${!embedded ? 'p-4 border-b' : 'pb-4'}`}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center">
            <Bell className="w-5 h-5 mr-2 text-gray-600" />
            Notifications
            {unreadCount > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {unreadCount}
              </span>
            )}
          </h3>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setFilter(filter === 'all' ? 'unread' : 'all')}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              {filter === 'all' ? 'Show Unread' : 'Show All'}
            </button>
            
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Mark All Read
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Notifications List */}
      <div 
        className={`${!embedded ? 'p-4' : ''} space-y-2 overflow-y-auto`}
        style={{ maxHeight }}
      >
        {notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No {filter === 'unread' ? 'unread ' : ''}notifications</p>
          </div>
        ) : (
          notifications.map(notification => (
            <div
              key={notification.id}
              className={`p-3 rounded-lg border transition-all ${
                notification.read 
                  ? 'bg-gray-50 border-gray-200' 
                  : 'bg-blue-50 border-blue-200'
              } hover:shadow-md`}
            >
              <div className="flex items-start">
                <div className={`${getIconColor(notification.type, notification.priority)} mr-3 mt-1`}>
                  {getIcon(notification.type)}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className={`font-medium text-sm ${
                        notification.read ? 'text-gray-700' : 'text-gray-900'
                      }`}>
                        {notification.title}
                      </h4>
                      <p className={`text-sm mt-1 ${
                        notification.read ? 'text-gray-500' : 'text-gray-600'
                      }`}>
                        {notification.message}
                      </p>
                      <div className="flex items-center mt-2 text-xs text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatDistanceToNow(parseISO(notification.created_at), { addSuffix: true })}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1 ml-2">
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="p-1 text-gray-400 hover:text-green-600"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {notification.action_url && (
                    <button
                      onClick={() => window.location.href = notification.action_url!}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      {notification.action_label || 'View Details'} →
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}